import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import cookies from 'js-cookie';
import io from 'socket.io-client';
import { CustomerDisplaySession, type CustomerDisplayContent } from 'washday-sdk';
import { connectCustomerDisplay } from '../services/customerDisplay.service';
import { CUSTOMER_DISPLAY_EVENTS as events, createCustomerDisplayBrand, type CustomerDisplaySnapshot } from '@washday/utilities';
import { loadDisplayIdentity, restoreDisplayIdentity, type DisplayIdentity } from './identity';
import { useGetStoreById } from '../hooks/stores/queries';

type DisplayContext = {
  identity: DisplayIdentity | null;
  status: string;
  session: CustomerDisplaySession | null;
  restore: (link: string) => Promise<void>;
  clear: () => void;
};
const Context = createContext<DisplayContext>({ identity: null, status: 'disconnected', session: null, restore: async () => {}, clear: () => {} });
export const useCustomerDisplay = () => useContext(Context);
export const displayChannelName = (id: string) => `washday.customer-display.${id}`;

export function CustomerDisplayProvider({ children }: { children: ReactNode }) {
  const auth = useSelector((state: { user: { token?: string; store?: string | { _id?: string }; user?: { _id?: string; store?: string; storeId?: string }; company?: { _id?: string; name?: string; logoUrl?: string } } }) => state.user);
  const location = useLocation();
  const selectedStoreId = new URLSearchParams(location.search).get('storeId');
  const sessionStoreId = typeof auth.store === 'string' ? auth.store : auth.store?._id;
  const fallbackStoreId = sessionStoreId || auth.user?.storeId || auth.user?.store || '';
  const scope = `${auth.company?._id || ''}:${auth.user?._id || ''}:${fallbackStoreId}`;
  const selectedId = selectedStoreId === 'all' ? '' : selectedStoreId;
  const [selection, setSelection] = useState({ scope, id: selectedId || fallbackStoreId });
  const lastStoreId = selection.scope === scope ? selection.id : fallbackStoreId;
  const { data: store } = useGetStoreById(Boolean(auth.token && auth.company?._id && lastStoreId), lastStoreId);
  const brand = createCustomerDisplayBrand(auth.company, store?._id === lastStoreId ? store : undefined);
  const brandRef = useRef(brand);
  brandRef.current = brand;
  const [identity, setIdentity] = useState<DisplayIdentity | null>(null);
  const [status, setStatus] = useState('disconnected');
  const [session, setSession] = useState<CustomerDisplaySession | null>(null);
  const clearRef = useRef(() => {});

  useEffect(() => {
    setSelection((previous) => previous.scope !== scope
      ? { scope, id: fallbackStoreId }
      : selectedId && previous.id !== selectedId ? { scope, id: selectedId } : previous);
  }, [scope, selectedId, fallbackStoreId]);
  useEffect(() => {
    let active = true;
    loadDisplayIdentity().then((value) => { if (active) setIdentity(value); }).catch(() => { if (active) setStatus('storage_error'); });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    const token = auth.token;
    if (!identity || !token || !auth.user?._id || !auth.company?._id || !lastStoreId) return;
    let stopped = false;
    let locallyAuthorized = false;
    let tokenExpiresAt = 0;
    try {
      const claims = JSON.parse(window.atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/'))) as { exp?: number };
      tokenExpiresAt = typeof claims.exp === 'number' ? claims.exp * 1000 : 0;
    } catch { /* Only verified, expiring sessions may continue local publication offline. */ }
    const instanceId = Array.from(crypto.getRandomValues(new Uint8Array(16)), (byte) => byte.toString(16).padStart(2, '0')).join('');
    let channel: BroadcastChannel | null = null;
    try { if (typeof BroadcastChannel !== 'undefined') channel = new BroadcastChannel(displayChannelName(identity.displayId)); } catch { /* Remote transport remains available. */ }
    const canPublishLocally = () => locallyAuthorized && Date.now() < tokenExpiresAt && cookies.get('token') === token && !stopped;
    const broadcast = (snapshot: CustomerDisplaySnapshot | null) => {
      if (!canPublishLocally() || !snapshot) return;
      channel?.postMessage({ type: events.status, connected: true, publisherInstanceId: instanceId });
      channel?.postMessage({ type: events.snapshot, snapshot });
    };
    const displaySession = new CustomerDisplaySession(instanceId, (snapshot) => {
      connection.publish(snapshot);
      broadcast(snapshot);
    });
    const connection = connectCustomerDisplay({
      socketFactory: (url, options) => io(url, options),
      companyId: auth.company._id, storeId: lastStoreId, ...identity, publisherInstanceId: instanceId,
      onStatus: (next) => {
        if (stopped) return;
        setStatus(next);
        if (next === 'connected') locallyAuthorized = true;
        if (next === 'forbidden' || next === 'busy') {
          locallyAuthorized = false;
          channel?.postMessage({ type: events.status, connected: false, publisherInstanceId: instanceId });
        }
        if (next === 'connected') broadcast(displaySession.snapshot);
      },
      onRequest: () => { if (displaySession.snapshot) connection.publish(displaySession.snapshot); }
    });
    if (channel) channel.onmessage = (event) => { if (event.data?.type === events.request) broadcast(displaySession.snapshot); };
    const pulse = setInterval(() => {
      // Stop publishing immediately if this tab's session was cleared elsewhere.
      if (cookies.get('token') !== token || Date.now() >= tokenExpiresAt) {
        channel?.postMessage({ type: events.status, connected: false, publisherInstanceId: instanceId });
        locallyAuthorized = false; displaySession.clear(); connection.dispose();
      }
      else broadcast(displaySession.snapshot);
    }, 5_000);
    clearRef.current = () => { displaySession.clear(); locallyAuthorized = false; connection.dispose(); };
    setSession(displaySession);
    displaySession.setBrand(brandRef.current);
    return () => {
      displaySession.clear();
      channel?.postMessage({ type: events.status, connected: false, publisherInstanceId: instanceId });
      stopped = true;
      locallyAuthorized = false;
      clearInterval(pulse);
      displaySession.dispose();
      connection.dispose();
      channel?.close();
      clearRef.current = () => {};
      setSession(null);
    };
  }, [identity, auth.token, auth.user?._id, auth.company?._id, lastStoreId]);

  useEffect(() => { session?.setBrand(brandRef.current); }, [session, brand.name, brand.logoUrl, brand.storeName]);

  const restore = useCallback(async (link: string) => { setIdentity(await restoreDisplayIdentity(link)); }, []);
  const clear = useCallback(() => clearRef.current(), []);
  return <Context.Provider value={{ identity, status, session, restore, clear }}>{children}</Context.Provider>;
}

export type { CustomerDisplayContent };
